'use client';

import React, { useEffect, useState } from 'react';
import { getLiveHighlightsWithUrls } from '../../../../lib/api/services/livestream';
import { getDownloadLink, updateDownloadStatus, updateCopyLinkStatus } from '../../../../lib/api/services/interaction';
import VideoPlayer from '../../../../components/VideoPlayer';
import { Download, Copy, Check } from 'lucide-react';
import Image from 'next/image';

interface HighlightData {
  highlight_id: string;
  highlight_url: string | null;
  highlight_title: string | null;
  highlight_description: string | null;
}

export default function Page({ params }: { params: { livestreamId: string, highlightId: string } }) {
  const [highlights, setHighlights] = useState<HighlightData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [copiedHighlightIds, setCopiedHighlightIds] = useState<Set<string>>(new Set());
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [successIds, setSuccessIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const data = await getLiveHighlightsWithUrls(params.livestreamId);
        setHighlights(data.filter((h: HighlightData) => h.highlight_url !== null));
        setIsLoading(false);
      } catch (err) {
        setError('Failed to load highlights');
        setIsLoading(false);
      }
    };

    fetchHighlights();
  }, [params.livestreamId]);

  // Add scroll into view effect when component mounts
  useEffect(() => {
    if (!isLoading && highlights.length > 0 && params.highlightId) {
      const highlightElement = document.getElementById(`highlight-${params.highlightId}`);
      if (highlightElement) {
        highlightElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [params.highlightId, isLoading, highlights]);

  const handlePageCopy = async () => {
    const shareUrl = `${window.location.origin}/live/${params.livestreamId}/all`;

    await updateCopyLinkStatus({
      highlight_id: params.highlightId
    });
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link');
    }
  };

  const handleHighlightCopy = async (highlight: HighlightData) => {
    if (!highlight.highlight_url) return;

    await updateCopyLinkStatus({
      highlight_id: highlight.highlight_id
    });
    
    try {
      await navigator.clipboard.writeText(highlight.highlight_url);
      setCopiedHighlightIds(prev => new Set(prev).add(highlight.highlight_id));
      setTimeout(() => {
        setCopiedHighlightIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(highlight.highlight_id);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy highlight link');
    }
  };

  const handleDownload = async (highlight: HighlightData) => {
    setDownloadingIds(prev => new Set(prev).add(highlight.highlight_id));
    try {
        // Get download link from API
        const downloadResponse = await getDownloadLink({
            highlight_id: highlight.highlight_id,
            page: "live"
        });

        const response = await fetch(downloadResponse.url);
        if (!response.ok) throw new Error('Failed to fetch video');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `highlight-${highlight.highlight_title}.mp4`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        await updateDownloadStatus({
            highlight_id: highlight.highlight_id
        });
        
        setDownloadError(null);
        setDownloadingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(highlight.highlight_id);
            return newSet;
        });
        setSuccessIds(prev => new Set(prev).add(highlight.highlight_id));
        // Reset success state after 2 seconds
        setTimeout(() => {
            setSuccessIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(highlight.highlight_id);
                return newSet;
            });
        }, 2000);
    } catch (err) {
        console.error('Failed to download video:', err);
        setDownloadError('Failed to download video');
        setDownloadingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(highlight.highlight_id);
            return newSet;
        });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#171717] flex items-center justify-center">
        <div className="text-center">
          <div className="text-white text-xl">Loading highlights...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#171717] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <div className="text-red-500 text-xl font-bold mb-2">{error}</div>
          <p className="text-gray-400 mb-6">Something went wrong while loading the highlights. Please try again later.</p>
          <a
            href="https://saved.gg"
            className="bg-gradient-to-r from-[#7984ff] to-[#cc8aff] text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
          >
            <span>Return to Saved.gg</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  if (highlights.length === 0) {
    return (
      <div className="min-h-screen bg-[#171717] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-gray-500 text-6xl mb-4">🎬</div>
          <div className="text-white text-xl font-bold mb-2">No Highlights Found</div>
          <p className="text-gray-400 mb-6">There are no highlights available for this livestream yet.</p>
          <a
            href="https://saved.gg"
            className="bg-gradient-to-r from-[#7984ff] to-[#cc8aff] text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
          >
            <span>Create Highlights</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[#171717]">
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">        
        <div className="absolute inset-0 flex flex-wrap gap-2 opacity-[0.02] text-white -mr-[20%] min-w-[120%]">
          {Array.from({ length: 100 }).map((_, i) => (
            <div key={i} className="text-8xl font-bold whitespace-nowrap">
              Saved.gg
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="z-50 top-0">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
          <div className="flex items-center">
            <a href="https://saved.gg">
              <Image
                src="https://storage.googleapis.com/saved_media_assets/SavedLogo.png"
                alt="Saved.gg Logo"
                width={128}
                height={32}
                priority
                className="h-6 sm:h-8 w-auto"
              />
            </a>
            <span className="ml-2 sm:ml-4 mt-3 text-white text-sm sm:text-xl font-bold">
              AI-Powered Stream Highlights
            </span>
          </div>
          <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
            <button
              onClick={handlePageCopy}
              className="bg-[#2E2E2E] hover:bg-[#3E3E3E] text-white px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 border border-white/10 w-full sm:w-auto justify-center"
            >
              {isCopied ? (
                <>
                  <Check className="w-4 h-4 text-[#7984ff]" />
                  <span className="sm:inline">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="sm:inline">Copy Link</span>
                </>
              )}
            </button>
            <a
              href="https://saved.gg"
              className="bg-gradient-to-r from-[#7984ff] to-[#cc8aff] text-[#ffffff] px-4 sm:px-8 py-2.5 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 hover:shadow-[0_8px_30px_rgb(210,77,255,0.3)] flex items-center gap-2 sm:gap-3 border border-white/10 w-full sm:w-auto justify-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Start Creating Highlights</span>
              <svg 
                className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >  
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 7l5 5m0 0l-5 5m5-5H6" 
                />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Highlights Container */}
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="flex flex-col md:flex-row overflow-x-auto gap-4 pb-4 snap-x snap-mandatory p-2">
          {highlights.map((highlight) => (
            <div
              key={highlight.highlight_id}
              id={`highlight-${highlight.highlight_id}`}
              className={`w-full md:w-[320px] md:flex-none snap-center bg-[#1E1E1E] rounded-lg border border-gray-800 transition-all duration-300 p-[2px] ${
                params.highlightId === highlight.highlight_id 
                  ? 'ring-2 ring-[#7984ff] ring-opacity-70 shadow-lg shadow-[#7984ff]/20' 
                  : ''
              }`}
            >
              <div className="h-full w-full bg-[#1E1E1E] rounded-lg overflow-hidden">
                {/* Video Player */}
                <div className="aspect-[9/16] mt-3 mx-3">
                  <VideoPlayer videoUrl={highlight.highlight_url || ''} />
                </div>

                {/* Highlight Info */}
                <div className="p-4">
                  {highlight.highlight_title && (
                    <h2 className="text-white text-lg font-semibold mb-2 truncate">
                      {highlight.highlight_title}
                    </h2>
                  )}
                  {highlight.highlight_description && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                      {highlight.highlight_description}
                    </p>
                  )}
                  
                  <div className="flex gap-2">
                    {/* Download Button */}
                    <button
                      onClick={() => handleDownload(highlight)}
                      disabled={downloadingIds.has(highlight.highlight_id)}
                      className={`flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl ${
                        successIds.has(highlight.highlight_id)
                          ? 'bg-green-600 hover:bg-green-700 hover:scale-[1.02] shadow-green-500/20'
                          : downloadingIds.has(highlight.highlight_id)
                          ? 'bg-blue-500 cursor-wait shadow-blue-500/20'
                          : 'bg-blue-600 hover:bg-blue-700 hover:scale-[1.02] shadow-blue-500/20'
                      } text-white`}
                    >
                      {successIds.has(highlight.highlight_id) ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Downloaded!
                        </>
                      ) : downloadingIds.has(highlight.highlight_id) ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </>
                      )}
                    </button>
                    {/* Copy Highlight Link Button */}
                    <button
                      onClick={() => handleHighlightCopy(highlight)}
                      className="bg-[#2E2E2E] hover:bg-[#3E3E3E] text-white px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 flex items-center justify-center border border-white/10"
                    >
                      {copiedHighlightIds.has(highlight.highlight_id) ? (
                        <>
                          <Check className="w-4 h-4 text-[#7984ff]" />
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 